// TMOS Provisioning Profile Service
// Date: December 20, 2025
// Purpose: Frontend service for provisioning profile management

import api from './api';

export type TemplateType = 'ROADMAP' | 'TRANSFORMATION' | 'KPI' | 'HIERARCHY' | 'ROLE' | 'POSITION' | 'PROCESS_AREAS' | 'RISK' | 'STAKEHOLDER' | 'CRM_LIST';
export type ProvisionScope = 'TENANT' | 'USER';

export interface ProvisioningProfile {
  id: string;
  profile_code: string;
  profile_name: string;
  profile_description?: string;
  is_admin_profile: boolean;
  is_active: boolean;
  provision_scope?: ProvisionScope;
  created_at: string;
  updated_at: string;
  templates?: ProvisioningProfileTemplate[];
  roles?: ProvisioningProfileRole[];
}

export interface ProvisioningProfileRole {
  id: string;
  profile_id: string;
  role_id: string;
  role_name?: string;
  display_name?: string;
  description?: string;
  provision_order: number;
  provision_scope?: ProvisionScope;
  created_at: string;
  updated_at: string;
}

export interface PlatformRole {
  id: string;
  role_name: string;
  display_name?: string;
  description?: string;
}

export interface ProvisioningProfileTemplate {
  id: string;
  profile_id: string;
  template_type: TemplateType;
  template_id: string;
  template_name?: string;
  is_required: boolean;
  provision_order: number;
  provision_scope?: ProvisionScope;
  created_at: string;
  updated_at: string;
}

export interface AssociateTemplateRequest {
  profileId: string;
  templateType: TemplateType;
  templateId: string;
  templateName?: string;
  isRequired?: boolean;
  provisionOrder?: number;
  provisionScope?: ProvisionScope;
}

class ProvisioningProfileService {
  /**
   * Get all provisioning profiles with details
   */
  async getAllProfiles(): Promise<ProvisioningProfile[]> {
    const response = await api.get('/provisioning-profiles');
    return response.data.profiles || [];
  }

  /**
   * Get profile by code with details
   */
  async getProfileByCode(profileCode: string): Promise<ProvisioningProfile | null> {
    const response = await api.get(`/provisioning-profiles/${profileCode}`);
    return response.data.profile || null;
  }

  /**
   * Associate template with profile
   */
  async associateTemplate(request: AssociateTemplateRequest): Promise<ProvisioningProfileTemplate> {
    const response = await api.post('/provisioning-profiles/templates', request);
    return response.data.template;
  }

  /**
   * Remove template association
   */
  async removeTemplateAssociation(
    profileId: string,
    templateType: TemplateType,
    templateId: string
  ): Promise<void> {
    await api.delete('/provisioning-profiles/templates', {
      data: { profileId, templateType, templateId }
    });
  }

  /**
   * Update profile
   */
  async updateProfile(profileCode: string, updates: { profile_name?: string; profile_description?: string }): Promise<ProvisioningProfile> {
    const response = await api.put(`/provisioning-profiles/${profileCode}`, updates);
    return response.data.profile;
  }

  /**
   * Associate role with profile
   */
  async associateRole(request: { profileId: string; roleId: string; provisionOrder: number; provisionScope: ProvisionScope }): Promise<ProvisioningProfileRole> {
    const response = await api.post('/provisioning-profiles/roles', request);
    return response.data.role;
  }

  /**
   * Remove role association
   */
  async removeRoleAssociation(profileId: string, roleId: string): Promise<void> {
    await api.delete('/provisioning-profiles/roles', {
      data: { profileId, roleId }
    });
  }

  /**
   * Get available platform roles
   */
  async getAvailableRoles(): Promise<PlatformRole[]> {
    const response = await api.get('/provisioning-profiles/available-roles');
    return response.data.roles || [];
  }

}

export const provisioningProfileService = new ProvisioningProfileService();

